import { JSX, useCallback, useEffect, useRef, useState } from "react";

import { ChevronDown20Regular } from "@fluentui/react-icons";

import "./MenuDropdown.scss";

export type MenuItem = {
  content: JSX.Element | string;
  notClickable?: boolean;
  persistOnClick?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  hasDivider?: boolean;
  className?: string;
};

type Props = {
  toggleText: string;
  hasCaret?: boolean;
  className?: string;
  menuItems: Partial<MenuItem>[];
  renderToggleContent?: (isOpen: boolean) => JSX.Element;
  onToggle?: (isOpen: boolean) => void;
};

export default function MenuDropdown({
  toggleText,
  hasCaret = true,
  className = "",
  menuItems,
  renderToggleContent,
  onToggle,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const menuDropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleMenu = useCallback((newIsOpen: boolean) => {
    setIsOpen(newIsOpen);
    if (onToggle) {
      onToggle(newIsOpen);
    }
  }, [onToggle]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuDropdownRef.current &&
        !menuDropdownRef.current.contains(event.target as Node)
      ) {
        toggleMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [toggleMenu]);

  const handleOnItemClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    item: Partial<MenuItem>,
  ) => {
    if (item.onClick) {
      item.onClick(e);
      if (!item.persistOnClick) {
        toggleMenu(false);
      }
    }
  };

  return (
    <div ref={menuDropdownRef}>
      <button className="dropdownToggle" onClick={() => toggleMenu(!isOpen)}>
        {toggleText}
        {renderToggleContent ? (
          renderToggleContent(isOpen)
        ) : (
          hasCaret && <ChevronDown20Regular className="chevronDownIcon" />
        )}
      </button>
      {isOpen && (
        <div className="dropdownMenu">
          {menuItems.map((item, i) => (
            <div key={i}>
              <div
                className={`dropdownItem ${item.className ?? ""} ${item.notClickable ? "notClickable" : ""}`}
                onClick={(e) => handleOnItemClick(e, item)}
              >
                {item.content}
              </div>
              {item.hasDivider && <span className="dropdownDivider" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
